import { useEffect, useState } from "react";
import { BookOpen, Play, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import LearnModeRecording from "./reading/LearnModeRecording";
import TestModeRecording from "./reading/TestModeRecording";

// Mirrors backend/modules_db.py::derive_sections' recording-module branch —
// an ayah's own admin-set `rule` (group tag) if set, else its own global
// number, so an ungrouped ayah still tests on its own. Only 2+ distinct
// values count as real sections (a lone value has nothing to sub-divide).
function deriveSections(m) {
  const seen = new Set(); const order = [];
  for (const p of m.passages || []) {
    for (const a of p.ayahs || []) {
      const key = a.rule || (a.n != null ? String(a.n) : null);
      if (key && !seen.has(key)) { seen.add(key); order.push(key); }
    }
  }
  return order.length > 1 ? order : [];
}

/**
 * Reading mode shell for module 9 — mirrors ReadingMode.jsx exactly (same
 * Learn/Test toggle, same shared Section picker sitting above both, same
 * visited-section Learn-gate) so the module 9 navigation feels identical to
 * every other module. What differs is entirely inside Learn/Test: module 9
 * renders continuous Quran verse text instead of a grid of short items.
 */
export default function ReadingModeRecording({ module: m, progress, onAttemptSaved }) {
  const { t } = useI18n();
  const [topMode, setTopMode] = useState("learn"); // "learn" | "test"
  const [testRunning, setTestRunning] = useState(false);

  const sections = deriveSections(m);
  const hasSections = sections.length > 0;
  const [selectedSection, setSelectedSection] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(`qf:section:${m.code}`) : null;
    return sections.includes(saved) ? saved : (sections[0] || null);
  });
  const [visitedSections, setVisitedSections] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(`qf:learn-visited:${m.code}`) : null;
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });

  useEffect(() => {
    if (topMode !== "learn" || !selectedSection || visitedSections.has(selectedSection)) return;
    const next = new Set(visitedSections);
    next.add(selectedSection);
    setVisitedSections(next);
    localStorage.setItem(`qf:learn-visited:${m.code}`, JSON.stringify([...next]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topMode, selectedSection]);

  const selectSection = (s) => {
    setSelectedSection(s);
    localStorage.setItem(`qf:section:${m.code}`, s);
  };

  const sectionUnlocked = !hasSections || visitedSections.has(selectedSection);

  return (
    <>
      {hasSections && (
        <div className="rounded-2xl bg-white border border-[#E7E0D2] shadow-sm p-3 mb-4" data-testid="shared-section-tabs">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4B5563] mb-2 px-1">{t("rd.section_picker_label")}</div>
          <div className="flex flex-wrap gap-2">
            {sections.map((s) => {
              const active = selectedSection === s;
              const visited = visitedSections.has(s);
              const passed = Boolean(progress?.sections?.[s]?.passed);
              return (
                <button key={s} data-testid={`shared-section-tab-${s}`}
                  onClick={() => !testRunning && selectSection(s)}
                  disabled={testRunning}
                  title={testRunning ? "Finish or reset the current test first" : undefined}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    active ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white text-[#1F2937] border-[#E7E0D2] hover:border-[#064E3B]"
                  }`}>
                  {passed ? (
                    <span className="inline-block w-2 h-2 rounded-full bg-[#10B981]" title={t("cert.status_passed")} />
                  ) : visited ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : null}
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E7E0D2] shadow-sm p-3 sm:p-4" data-testid="top-mode-toggle">
        <div className="flex items-center gap-2">
          <button
            data-testid="top-mode-learn"
            onClick={() => !testRunning && setTopMode("learn")}
            disabled={testRunning}
            title={testRunning ? "Finish or reset the current test first" : undefined}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed ${
              topMode === "learn" ? "bg-[#D97706] text-white border-[#D97706]" : "bg-white border-[#E7E0D2] hover:border-[#D97706] text-[#1F2937]"
            }`}>
            <BookOpen className="w-4 h-4" /> {t("rd.learn")}
          </button>
          <button
            data-testid="top-mode-test"
            onClick={() => !testRunning && setTopMode("test")}
            disabled={testRunning}
            title={testRunning ? "Finish or reset the current test first" : undefined}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed ${
              topMode === "test" ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]"
            }`}>
            <Play className="w-4 h-4" /> {t("rd.test")}
          </button>
        </div>
      </div>

      <div className="mt-4">
        {topMode === "learn"
          ? <LearnModeRecording module={m} hasSections={hasSections} selectedSection={selectedSection}
              sections={sections} onSelectSection={selectSection} />
          : <TestModeRecording module={m} progress={progress} onAttemptSaved={onAttemptSaved} onRunningChange={setTestRunning}
              hasSections={hasSections} selectedSection={selectedSection} sectionUnlocked={sectionUnlocked} />}
      </div>
    </>
  );
}
